import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { message } from "antd";
import Utilities from "../../dependencies/custom/react-utilities";
import domainSettings from "../../dependencies/custom/settings";

const usePreschoolGrading = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [learningAreas, setLearningAreas] = useState([]);
  const [preschoolGrades, setPreschoolGrades] = useState([]);

  // Learning Areas (Preschool Grading Columns)
  const fetchLearningAreas = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.academics.preschool_learning_areas,
        null,
        navigate,
      );
      if (response.ok) {
        setLearningAreas(response.data || []);
        return response.data;
      } else {
        message.error(response.msg || "Failed to fetch learning areas");
        return null;
      }
    } catch (error) {
      console.error("Error fetching learning areas:", error);
      message.error("An error occurred while fetching learning areas");
      return null;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const saveLearningArea = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.preschool_learning_areas,
          data,
          navigate,
        );
        if (response.ok) {
          message.success(
            data.id ? "Learning area updated" : "Learning area added",
          );
          return response.data;
        } else {
          message.error(response.msg || "Failed to save learning area");
          return null;
        }
      } catch (error) {
        console.error("Error saving learning area:", error);
        message.error("An error occurred while saving learning area");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const deleteLearningArea = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = `${domainSettings.urls.academics.preschool_learning_areas}/${id}`;
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Learning area deleted");
          return true;
        } else {
          message.error(response.msg || "Failed to delete learning area");
          return false;
        }
      } catch (error) {
        console.error("Error deleting learning area:", error);
        message.error("An error occurred while deleting learning area");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  // Preschool Grades (Preschool Grading Remarks)
  const fetchPreschoolGrades = useCallback(async () => {
    setLoading(true);
    try {
      const response = await Utilities.fetchWithAuth(
        "GET",
        domainSettings.urls.academics.preschool_grades,
        null,
        navigate,
      );
      if (response.ok) {
        setPreschoolGrades(response.data || []);
        return response.data;
      } else {
        message.error(response.msg || "Failed to fetch nursery grades");
        return null;
      }
    } catch (error) {
      console.error("Error fetching preschool grades:", error);
      message.error("An error occurred while fetching nursery grades");
      return null;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const savePreschoolGrade = useCallback(
    async (data) => {
      setLoading(true);
      try {
        const response = await Utilities.fetchWithAuth(
          "POST",
          domainSettings.urls.academics.preschool_grades,
          data,
          navigate,
        );
        if (response.ok) {
          message.success(
            data.id ? "Nursery grade updated" : "Nursery grade added",
          );
          return response.data;
        } else {
          message.error(response.msg || "Failed to save nursery grade");
          return null;
        }
      } catch (error) {
        console.error("Error saving preschool grade:", error);
        message.error("An error occurred while saving nursery grade");
        return null;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const deletePreschoolGrade = useCallback(
    async (id) => {
      setLoading(true);
      try {
        const url = `${domainSettings.urls.academics.preschool_grades}/${id}`;
        const response = await Utilities.fetchWithAuth(
          "DELETE",
          url,
          null,
          navigate,
        );
        if (response.ok) {
          message.success("Nursery grade deleted");
          return true;
        } else {
          message.error(response.msg || "Failed to delete nursery grade");
          return false;
        }
      } catch (error) {
        console.error("Error deleting preschool grade:", error);
        message.error("An error occurred while deleting nursery grade");
        return false;
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  return {
    loading,
    learningAreas,
    preschoolGrades,
    fetchLearningAreas,
    saveLearningArea,
    deleteLearningArea,
    fetchPreschoolGrades,
    savePreschoolGrade,
    deletePreschoolGrade,
  };
};

export default usePreschoolGrading;
